import { Type } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { format, parseISO } from 'date-fns';
import { PickListFieldConfig } from '~ngx-shared/formly/types/pick-list-type/pick-list-type.component';
import { StepFieldConfig } from '~ngx-shared/formly/types/step-type/step-type.component';
import { AutoCompleteSelectFieldConfig } from '../types/auto-complete-select-type/auto-complete-select-type.component';
import { AutoCompleteFieldConfig } from '../types/auto-complete-type/auto-complete-type.component';
import { CheckboxFieldConfig } from '../types/checkbox-type/checkbox-type.component';
import { ColorPickerFieldConfig } from '../types/color-type/color-type.component';
import { DatepickerFieldConfig } from '../types/date-type/date-type.component';
import { EditorFieldConfig } from '../types/editor-type/editor-type.component';
import { FileFieldConfig } from '../types/file-type/file-type.component';
import { InputMaskFieldConfig } from '../types/input-mask-type/input-mask-type.component';
import { LabelFieldConfig } from '../types/label-type/label-type.component';
import { MultiSelectFieldConfig } from '../types/multi-select-type/multi-select-type.component';
import { NumberFieldConfig } from '../types/number-type/number-type.component';
import { RadioFieldConfig } from '../types/radio-type/radio-type.component';
import { SignaturePadFieldConfig } from '../types/signature-pad-type/signature-pad-type.component';
import { RepeatFieldConfig } from '../types/repeat-type/repeat-type.component';
import { SelectButtonFieldConfig } from '../types/select-button-type/select-button-type.component';
import { SelectFieldConfig } from '../types/select-type/select-type.component';
import { TableFieldConfig } from '../types/table-type/table-type.component';
import { TextAreaFieldConfig } from '../types/text-area-type/text-area-type.component';
import { TextFieldConfig } from '../types/text-type/text-type.component';
import { FieldWrapperProps } from '../wrappers/field-wrapper/field-wrapper.component';
import {
  GroupWrapperFieldConfig,
  GroupWrapperProps
} from '../wrappers/group-wrapper/group-wrapper.component';
import type { MjmlFieldConfig } from '../types/mjml-type/mjml-type.component';

export class FormlyUtil {
  static EUROPE_DATE_FORMAT = 'dd.MM.yyyy';
  static ISO_DATE_FORMAT = 'yyyy-MM-dd';
  static ISO_TIME_FORMAT = 'HH:mm:ss';
  static ISO_TIME_FORMAT_SHORT = 'HH:mm';
  static ISO_TIME_FORMAT_WITHOUT_SECONDS = 'HH:mm:00';

  static toIsoDateString(date: any, formatStr: string = FormlyUtil.ISO_DATE_FORMAT): string | null {
    if (!date) {
      return null;
    }
    return format(date, formatStr);
  }

  static toIsoTimeString(date: any): string {
    return format(date, FormlyUtil.ISO_TIME_FORMAT_WITHOUT_SECONDS);
  }

  static toIsoTimeShortString(date: any): string {
    return format(date, FormlyUtil.ISO_TIME_FORMAT_SHORT);
  }

  static fromIsoDateString(
    date: string | Date | undefined,
    useDefault = true,
    addUTCString = false
  ): Date | undefined {
    if (!date) {
      if (useDefault) {
        return new Date();
      }
      return undefined;
    }
    if (typeof date === 'string') {
      return addUTCString ? parseISO(date + 'Z') : parseISO(date);
    }
    return date;
  }

  static fromIsoTimeString(time: string | Date | undefined): Date {
    // Check if it is HH:mm:ss then convert to date
    if (typeof time === 'string' && time.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return parseISO(`1970-01-01T${time}`);
    }

    return new Date();
  }

  // The tab type binds its active index to a query parameter, so the collected indexes are what open
  // every tab between the form and the control.
  static findFirstInvalidField(
    fields?: FormlyFieldConfig[]
  ): { field: FormlyFieldConfig; tabIndexes: Record<string, number> } | undefined {
    if (!fields) {
      return undefined;
    }
    for (const field of fields) {
      if (field.hide) {
        continue;
      }
      // A repeat or a group is invalid whenever a child is, so descend before testing the key.
      if (field.fieldGroup) {
        for (let index = 0; index < field.fieldGroup.length; index++) {
          const found = FormlyUtil.findFirstInvalidField([field.fieldGroup[index]]);
          if (found) {
            if (field.type === 'tab') {
              found.tabIndexes[field.props?.['query'] || 't'] = index;
            }
            return found;
          }
        }
      } else if (field.key && field.formControl?.invalid) {
        return { field, tabIndexes: {} };
      }
    }
    return undefined;
  }

  static findKeyRecursively(
    key: string,
    fields?: FormlyFieldConfig[]
  ): FormlyFieldConfig | undefined {
    if (!fields) {
      return undefined;
    }
    for (const field of fields) {
      if (field.key === key) {
        return field;
      }
      const result = FormlyUtil.findKeyRecursively(key, field.fieldGroup);
      if (result) {
        return result;
      }
    }
    return undefined;
  }

  static createRow(
    fieldGroup: FormlyFieldConfig<FieldWrapperProps & { [p: string]: any }>[] | undefined
  ): FormlyFieldConfig {
    return {
      fieldGroupClassName: 'formly-row flex gap-3 flex-wrap',
      fieldGroup: fieldGroup?.map(field => {
        if (!field.className) {
          field.className = 'flex-1';
        }
        return field;
      })
    };
  }

  static createGroup(
    label: string,
    fieldGroup: FormlyFieldConfig<GroupWrapperProps & { [p: string]: any }>[] | undefined
  ): GroupWrapperFieldConfig {
    return this.createGroupWithConfig(
      {
        props: {
          label
        }
      },
      fieldGroup
    );
  }

  static createGroupWithKeyConfig(
    key: string,
    config?: GroupWrapperFieldConfig,
    fieldGroup?: FormlyFieldConfig[] | undefined
  ): GroupWrapperFieldConfig {
    return {
      key,
      ...this.createGroupWithConfig(config, fieldGroup)
    };
  }

  static createGroupWithConfig(
    config?: GroupWrapperFieldConfig,
    fieldGroup?: FormlyFieldConfig[] | undefined
  ): GroupWrapperFieldConfig {
    return {
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      fieldGroup,
      wrappers: ['group-wrapper']
    };
  }

  static createTabbed(
    fieldGroup: FormlyFieldConfig<FormlyFieldProps & { [p: string]: any }>[] | undefined,
    query = 't'
  ): FormlyFieldConfig {
    return {
      type: 'tab',
      props: {
        query
      },
      fieldGroup
    };
  }

  static createTab(
    label: string,
    fieldGroup: FormlyFieldConfig<FormlyFieldProps & { [p: string]: any }>[] | undefined
  ): FormlyFieldConfig {
    return {
      props: {
        label: label
      },
      fieldGroup
    };
  }

  static createStepper(
    config: StepFieldConfig,
    fieldGroup: FormlyFieldConfig[] | undefined
  ): FormlyFieldConfig {
    return {
      ...config,
      fieldGroup,
      type: 'step'
    };
  }

  static createStep(
    label: string,
    fieldGroup: FormlyFieldConfig<FormlyFieldProps & { [p: string]: any }>[] | undefined
  ): FormlyFieldConfig {
    return {
      props: {
        label: label
      },
      fieldGroup
    };
  }

  //
  // FIELDS
  //

  static createRepeat(
    config: RepeatFieldConfig,
    fieldGroup: FormlyFieldConfig<FieldWrapperProps & { [p: string]: any }>[] | undefined
  ): FormlyFieldConfig {
    let defaultValue: any = config.defaultValue || [];
    if (config.props?.initialCount) {
      defaultValue = [];
      for (let i = 0; i < config.props.initialCount; i++) {
        defaultValue.push({});
      }
    }
    return {
      ...config,
      type: 'repeat',
      defaultValue: defaultValue,
      fieldArray: {
        fieldGroup
      }
    };
  }

  static createGenericField<T = FormlyFieldConfig<FieldWrapperProps>>(
    key: string,
    type:
      | string
      | Type<FieldType<FormlyFieldConfig<FieldWrapperProps & { [p: string]: any }>>>
      | undefined,
    config?: T
  ): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      key,
      type
    };
  }

  static createTextField(key: string, config?: TextFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'text'
    };
  }

  static createCheckboxField(key: string, config?: CheckboxFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      defaultValue: false,
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'checkbox'
    };
  }

  static createRadioField(key: string, config?: RadioFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      defaultValue: undefined,
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'radio'
    };
  }

  static createToggleField(key: string, config?: CheckboxFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      defaultValue: false,
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'toggle'
    };
  }

  static createDatePickerField(key: string, config?: DatepickerFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props,
        showButtonBar: true
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'date-picker'
    };
  }

  static createInputMaskField(key: string, config?: InputMaskFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'input-mask'
    };
  }

  static createDateTimePickerField(key: string, config?: DatepickerFieldConfig): FormlyFieldConfig {
    return {
      ...FormlyUtil.createDatePickerField(key, config),
      props: {
        ...config?.props,
        showTime: true,
        showButtonBar: true
      }
    };
  }

  static createTimePickerField(key: string, config?: DatepickerFieldConfig): FormlyFieldConfig {
    return {
      ...FormlyUtil.createDatePickerField(key, config),
      props: {
        ...config?.props,
        timeOnly: true
      }
    };
  }

  static createColorPickerField(key: string, config?: ColorPickerFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'color-picker'
    };
  }

  static createSelectField(key: string, config?: SelectFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'select'
    };
  }

  static createSelectButtonField(key: string, config?: SelectButtonFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'select-button'
    };
  }

  static createMultiSelectField(key: string, config?: MultiSelectFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'multi-select'
    };
  }

  static createPickListField(key: string, config?: PickListFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'pick-list'
    };
  }

  static createNumberField(key: string, config?: NumberFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'number'
    };
  }

  static createAutoCompleteField(key: string, config?: AutoCompleteFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        options: [],
        placeholder: 'search',
        valueProp: (option: any) => option,
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'autocomplete'
    };
  }

  static createAutoCompleteSelectField(
    key: string,
    config?: AutoCompleteSelectFieldConfig
  ): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        options: [],
        placeholder: 'search',
        valueProp: (option: any) => option,
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'autocomplete-select'
    };
  }

  static createTextAreaField(key: string, config?: TextAreaFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'textarea'
    };
  }

  static createEditorField(key: string, config?: EditorFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'editor'
    };
  }

  static createMjmlField(key: string, config?: MjmlFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'mjml'
    };
  }

  static createTable(
    config: TableFieldConfig,
    fieldGroup: FormlyFieldConfig<FieldWrapperProps & { [p: string]: any }>[] | undefined
  ): FormlyFieldConfig {
    let defaultValue: any = undefined;
    if (config.props?.initialCount) {
      defaultValue = [];
      for (let i = 0; i < config.props.initialCount; i++) {
        defaultValue.push({});
      }
    }
    return {
      ...config,
      className: 'w-full overflow-visible',
      type: 'table',
      defaultValue: defaultValue,
      fieldArray: {
        fieldGroup
      }
    };
  }

  static createLabelField(key: string, config?: LabelFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      props: {
        ...config?.props,
        hideLabel: true
      },
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'label'
    };
  }

  static createFileField(key: string, config?: FileFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'file'
    };
  }

  static createSignaturePadField(key: string, config?: SignaturePadFieldConfig): FormlyFieldConfig {
    return {
      ...this._defaultConfig(),
      ...config,
      expressions: {
        ...config?.expressions
      },
      key,
      type: 'signature-pad'
    };
  }

  private static _defaultConfig(): FormlyFieldConfig {
    return {};
  }
}
